import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { SelectValue } from 'antd/es/select';

import './index.less';

import { alertsSelectorAdd } from '../../recoil';
import { defaultApi } from '../../apis';
import IbButton from '../common/IbButton';
import IbModal, { IbModalPosition } from '../common/IbModal';
import {
  AgentModel,
  ConnectorApiKeyAuthTypeEnum,
  ConnectorModel,
  DataInterfaceType,
  ELMA365ConnectorSettingsOutputTypeEnum,
  ELMA365ToolsetModel,
  ELMA365ToolsetStatus,
  ExchangeDependency,
  ExchangeObjectType,
  IngestionSourceModelOutput,
  IngestionSourceParametersType,
  ProjectModel,
  ProviderModel,
  ToolsetSettingsType,
} from '../../../gamma-api';
import { getTranslateFunc } from '../../utils/stringUtil';
import { LARGE_PAGE_SIZE } from '../../constants';
import IbSelect from '../common/IbSelect';
import IbTypography from '../common/IbTypography';
import IbIcon, { IbIconName } from '../common/IbIcon';
import IbTooltip from '../common/IbTooltip';
import IbScrollSpy, { IIbScrollSpySection } from '../common/IbScrollSpy';
import IbDivider from '../common/IbDivider';
import IbConfirmModal from '../common/IbConfirmModal';
import { getToolsetTypeIconName, getToolsetTypeName } from '../../pages/agents/AgentPage/ToolsetsTab/utils';
import IbCheckbox from '../common/IbCheckbox';

import {
  MAIN_CLASS_NAME,
  SECTION_CLASS_NAME,
  SECTION_HEADER_CLASS_NAME,
  SELECT_LABEL_CLASS_NAME,
  GROUP_CLASS_NAME,
  ROW_CLASS_NAME,
  ROW_ITEM_OBJECT_CLASS_NAME,
  ROW_ITEM_CLASS_NAME,
  ROW_ICON_CLASS_NAME,
  UNSELECTED_ALERT_CLASS_NAME,
  TITLE_CLASS_NAME,
  UNSELECTED_ALERT_BUTTON_CLASS_NAME,
  ROW_ITEM_PATH_CLASS_NAME,
  ROW_ITEM_TOOLTIP_CLASS_NAME,
  GROUP_TITLE_CLASS_NAME,
  SUB_GROUP_CLASS_NAME,
  SUB_GROUP_TITLE_CLASS_NAME,
  EXTRA_CLASS_NAME,
  EXTRA_CHECKBOX_CLASS_NAME,
  INGEST_SOURCES_EXTRA_KEY,
  DELETE_SOURCES_EXTRA_KEY,
} from './const';

enum DependenciesModalStep {
  Setup = 'Setup',
  Configure = 'Configure',
}

interface IDependenciesModalProps {
  visible: boolean;
  dependencies: ExchangeDependency[];
  folderId?: string;
  entityId?: string;
  entityType?: ExchangeObjectType;
  onChange?: (dependencies: ExchangeDependency[]) => void;
  onFinish?: () => void;
  onCancel?: () => void;
}

const renderTooltip = (text: string | null | undefined, options?: { disabled?: boolean }) => (
  <IbTooltip className={ROW_ITEM_TOOLTIP_CLASS_NAME} placement="right" title={text}>
    <IbTypography.Paragraph ellipsis disabled={options?.disabled} type="secondary">
      {text}
    </IbTypography.Paragraph>
  </IbTooltip>
);

const DependenciesModal: React.FC<IDependenciesModalProps> = ({
  visible,
  dependencies,
  folderId,
  entityId,
  entityType,
  onChange,
  onFinish,
  onCancel,
}) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const translate = getTranslateFunc('Dependencies: Modal:');
  const errorRefsRef = useRef<HTMLDivElement[]>([]);

  const [providers, setProviders] = useState<ProviderModel[]>([]);
  const [connectors, setConnectors] = useState<ConnectorModel[]>([]);
  const [toolkits, setToolkits] = useState<ELMA365ToolsetModel[]>([]);
  const [sources, setSources] = useState<IngestionSourceModelOutput[]>([]);
  const [projects, setProjects] = useState<ProjectModel[]>([]);
  const [agents, setAgents] = useState<AgentModel[]>([]);

  const [providersLoading, setProvidersLoading] = useState(true);
  const [connectorsLoading, setConnectorsLoading] = useState(true);
  const [toolkitsLoading, setToolkitsLoading] = useState(true);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [agentsLoading, setAgentsLoading] = useState(true);

  const [confirmVisible, setConfirmVisible] = useState(false);
  const [step, setStep] = useState<DependenciesModalStep>(DependenciesModalStep.Setup);

  const setupStepProjects = useMemo(
    () =>
      new Map(
        dependencies
          .filter((dependency) => dependency.object_code && dependency.object_type === ExchangeObjectType.Project)
          .map((dependency) => [dependency.object_code, dependency])
      ),
    [dependencies]
  );

  const setupStepSources = useMemo(() => {
    const result: Map<string, Map<string, ExchangeDependency>> = new Map();

    for (const dependency of dependencies) {
      if (!dependency.object_code || dependency.object_type !== ExchangeObjectType.Source) {
        continue;
      }

      let sources = result.get(dependency.parent_code);
      if (!sources) {
        sources = new Map();
        result.set(dependency.parent_code, sources);
      }

      sources.set(dependency.object_code, dependency);
    }

    return result;
  }, [dependencies]);

  const setupStepDependencies = useMemo(
    () =>
      Array.from(
        new Map(
          dependencies
            .filter((dependency) => {
              if (!dependency.object_code) {
                return false;
              }

              return ([
                ExchangeObjectType.Agent,
                ExchangeObjectType.Project,
                ExchangeObjectType.Source,
                ExchangeObjectType.Toolkit,
              ] as ExchangeObjectType[]).includes(dependency.object_type);
            })
            .map((d) => [d.object_code, d])
        ).values()
      ),
    [dependencies]
  );

  const configureStepDependencies = useMemo(
    () =>
      dependencies.filter(
        (dependency) =>
          dependency.object_type === ExchangeObjectType.Provider ||
          dependency.object_type === ExchangeObjectType.Connector
      ),
    [dependencies]
  );

  const providerOptions = useMemo(
    () => providers.map((provider) => ({ value: provider.provider_id, label: `${provider.name} - ${provider.title}` })),
    [providers]
  );

  const connectorOptions = useMemo(
    () =>
      connectors.map((connector) => ({
        types: [connector.settings.type, connector.settings.apis.web.auth.type],
        value: connector.connector_id,
        label: `${connector.name} - ${connector.settings.apis.web.url}`,
      })),
    [connectors]
  );

  const getConnectorOptions = useCallback(
    (parentType: ExchangeObjectType) => {
      if (parentType !== ExchangeObjectType.Source) {
        return connectorOptions;
      }

      return connectorOptions.filter(
        (option) =>
          option.types[0] === ELMA365ConnectorSettingsOutputTypeEnum.Elma365 &&
          option.types[1] === ConnectorApiKeyAuthTypeEnum.ApiKey
      );
    },
    [connectorOptions]
  );

  const dependencyIndexMap = useMemo(() => new Map(dependencies.map((dependency, index) => [dependency, index])), [
    dependencies,
  ]);

  const unselectedCount = useMemo(() => {
    const existingIds = new Set([
      ...connectors.map((connector) => connector.connector_id),
      ...providers.map((provider) => provider.provider_id),
    ]);
    return configureStepDependencies.filter(
      (dependency) => !dependency.object_id || !existingIds.has(dependency.object_id)
    ).length;
  }, [configureStepDependencies, connectors, providers]);

  useEffect(() => {
    if (!visible) return;

    let cancelled = false;

    if (setupStepDependencies.length > 1) {
      setStep(DependenciesModalStep.Setup);
    } else {
      setStep(DependenciesModalStep.Configure);
    }

    const hasDependency = (type: ExchangeObjectType) => dependencies.some((d) => d.object_type === type);

    const fetchProviders = async () => {
      if (!hasDependency(ExchangeObjectType.Provider)) {
        setProvidersLoading(false);
        return;
      }
      setProvidersLoading(true);
      try {
        const response = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE);
        if (!cancelled) setProviders(response.data.items || []);
      } catch (err) {
        if (!cancelled) addAlert({ content: translate('Providers: Failed to load'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        if (!cancelled) setProvidersLoading(false);
      }
    };

    const fetchConnectors = async () => {
      if (!hasDependency(ExchangeObjectType.Connector)) {
        setConnectorsLoading(false);
        return;
      }
      setConnectorsLoading(true);
      try {
        const response = await defaultApi.searchConnectors(0, LARGE_PAGE_SIZE);
        if (!cancelled) setConnectors(response.data.items || []);
      } catch (err) {
        if (!cancelled) addAlert({ content: translate('Connectors: Failed to load'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        if (!cancelled) setConnectorsLoading(false);
      }
    };

    const fetchToolkits = async () => {
      if (!folderId || !hasDependency(ExchangeObjectType.Toolkit)) {
        setToolkitsLoading(false);
        return;
      }
      setToolkitsLoading(true);
      try {
        const response = await defaultApi.searchELMA365ToolkitToolsets(
          0,
          LARGE_PAGE_SIZE,
          undefined,
          undefined,
          [folderId],
          undefined,
          undefined,
          [ELMA365ToolsetStatus.Ready]
        );
        if (!cancelled) setToolkits(response.data.items || []);
      } catch (err) {
        if (!cancelled) addAlert({ content: translate('Toolkits: Failed to load'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        if (!cancelled) setToolkitsLoading(false);
      }
    };

    const fetchSources = async () => {
      if (!folderId || !hasDependency(ExchangeObjectType.Source)) {
        setSourcesLoading(false);
        return;
      }
      setSourcesLoading(true);
      try {
        const response = await defaultApi.searchSourcesIngestionSourcesGet(
          undefined,
          [folderId],
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          LARGE_PAGE_SIZE
        );
        if (!cancelled) setSources(response.data.items || []);
      } catch (err) {
        if (!cancelled) addAlert({ content: translate('Sources: Failed to load'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        if (!cancelled) setSourcesLoading(false);
      }
    };

    const fetchProjects = async () => {
      if (!folderId || !hasDependency(ExchangeObjectType.Project)) {
        setProjectsLoading(false);
        return;
      }
      setProjectsLoading(true);
      try {
        const response = await defaultApi.searchProjectsGeneralProjectsGet(
          undefined,
          [folderId],
          undefined,
          LARGE_PAGE_SIZE
        );
        if (!cancelled) setProjects(response.data.items || []);
      } catch (err) {
        if (!cancelled) addAlert({ content: translate('Projects: Failed to load'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    };

    const fetchAgents = async () => {
      if (!folderId || !hasDependency(ExchangeObjectType.Agent)) {
        setAgentsLoading(false);
        return;
      }
      setAgentsLoading(true);
      try {
        const response = await defaultApi.searchAgents(undefined, [folderId], undefined, undefined, LARGE_PAGE_SIZE);
        if (!cancelled) setAgents(response.data.items || []);
      } catch (err) {
        if (!cancelled) addAlert({ content: translate('Agents: Failed to load'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        if (!cancelled) setAgentsLoading(false);
      }
    };

    void fetchProviders();
    void fetchConnectors();
    void fetchToolkits();
    void fetchSources();
    void fetchProjects();
    void fetchAgents();

    return () => {
      cancelled = true;
    };
  }, [visible]);

  const onConfigureSelectChange = (index: number) => (value: SelectValue) => {
    if (!onChange) return;
    onChange(
      dependencies.map((item, idx) => (idx !== index ? item : { ...item, object_id: (value as string) || null }))
    );
  };

  const onSetupSelectChange = (objectCode: string) => (value: SelectValue) => {
    onChange?.(
      dependencies.map((item) =>
        item.object_code !== objectCode ? item : { ...item, object_id: (value as string) || null }
      )
    );
  };

  const onSetupExtraChange = (objectCode: string, extraKey: string) => (value: boolean) => {
    onChange?.(
      dependencies.map((item) =>
        item.object_code !== objectCode ? item : { ...item, extra_data: { ...item.extra_data, [extraKey]: value } }
      )
    );
  };

  const onNextClick = () => setStep(DependenciesModalStep.Configure);
  const onBackClick = () => setStep(DependenciesModalStep.Setup);

  const onFinishClick = () => {
    if (unselectedCount) {
      setConfirmVisible(true);
    } else {
      onFinish?.();
    }
  };

  const onConfirmFinish = () => {
    setConfirmVisible(false);
    onFinish?.();
  };

  const onConfirmCancel = () => setConfirmVisible(false);

  const onScrollToFirstError = () => {
    errorRefsRef.current[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const renderCreateNewLabel = () => (
    <div className={SELECT_LABEL_CLASS_NAME}>
      <IbIcon iconName="plus" />
      {translate('Select: Create new')}
    </div>
  );

  const getSelectOptions = <T,>(
    items: T[],
    getId: (item: T) => string,
    getLabel: (item: T) => string,
    currentObjectId: string,
    objectType: ExchangeObjectType
  ) => [
    ...items.map((item) => {
      const id = getId(item);
      return {
        value: id,
        label: getLabel(item),
        disabled:
          currentObjectId !== id &&
          setupStepDependencies.some(
            (dependency) => dependency.object_type === objectType && dependency.object_id === id
          ),
      };
    }),
    { value: '', label: renderCreateNewLabel() },
  ];

  const getConfigureSelectHasError = (dependency: ExchangeDependency) => {
    const objectId = dependency.object_id || '';
    if (dependency.object_type === ExchangeObjectType.Provider) {
      return !objectId || !providerOptions.some((option) => option.value === objectId);
    }
    if (dependency.object_type === ExchangeObjectType.Connector) {
      return !objectId || !getConnectorOptions(dependency.parent_type).some((option) => option.value === objectId);
    }
    return false;
  };

  const getObjectIconName = (dependency: ExchangeDependency): IbIconName | undefined => {
    if (dependency.object_type !== ExchangeObjectType.Source) {
      return undefined;
    }

    if (
      [
        IngestionSourceParametersType.Website,
        IngestionSourceParametersType.WebsiteUrls,
        IngestionSourceParametersType.WebsiteCrawler,
      ].includes(dependency.object_subtype)
    ) {
      return 'international';
    }

    if (dependency.object_subtype === IngestionSourceParametersType.File) {
      return 'paperclip';
    }

    if (dependency.object_subtype === IngestionSourceParametersType.Elma365) {
      return 'elma365';
    }

    if (dependency.object_subtype === IngestionSourceParametersType.Confluence) {
      return 'confluence';
    }
  };

  const renderObjectSelector = (dependency: ExchangeDependency, changeHandler: (value: SelectValue) => void) => {
    const objectId = dependency.object_id || '';
    const hasError = getConfigureSelectHasError(dependency);

    if (dependency.object_type === ExchangeObjectType.Provider) {
      return (
        <IbSelect
          loading={providersLoading}
          options={providerOptions}
          status={hasError ? 'error' : undefined}
          suffix={<IbIcon iconName="server" />}
          value={objectId}
          onChange={changeHandler}
        />
      );
    }

    if (dependency.object_type === ExchangeObjectType.Connector) {
      return (
        <IbSelect
          loading={connectorsLoading}
          options={getConnectorOptions(dependency.parent_type)}
          status={hasError ? 'error' : undefined}
          suffix={<IbIcon iconName="toolkit" />}
          value={objectId}
          onChange={changeHandler}
        />
      );
    }

    if (dependency.object_type === ExchangeObjectType.Toolkit) {
      const options = getSelectOptions(
        toolkits,
        (toolkit) => toolkit.toolset_id,
        (toolkit) => toolkit.name,
        objectId,
        ExchangeObjectType.Toolkit
      );
      return <IbSelect loading={toolkitsLoading} options={options} value={objectId} onChange={changeHandler} />;
    }

    if (dependency.object_type === ExchangeObjectType.Source) {
      const project = setupStepProjects.get(dependency.parent_code);
      let projectSources: IngestionSourceModelOutput[];
      if (project?.object_id) {
        projectSources = sources.filter((source) => source.project_id === project.object_id);
      } else if (entityId && entityType === ExchangeObjectType.Project) {
        projectSources = sources.filter((source) => source.project_id === entityId);
      } else {
        projectSources = [];
      }
      const projectSourceId = projectSources.some((source) => source.source_id === objectId) ? objectId : '';
      const options = getSelectOptions(
        projectSources,
        (source) => source.source_id,
        (source) => source.name,
        projectSourceId,
        ExchangeObjectType.Source
      );
      return <IbSelect loading={sourcesLoading} options={options} value={projectSourceId} onChange={changeHandler} />;
    }

    if (dependency.object_type === ExchangeObjectType.Project) {
      const options = getSelectOptions(
        projects,
        (project) => project.project_id,
        (project) => project.name,
        objectId,
        ExchangeObjectType.Project
      );
      return <IbSelect loading={projectsLoading} options={options} value={objectId} onChange={changeHandler} />;
    }

    if (dependency.object_type === ExchangeObjectType.Agent) {
      if (dependency.object_subtype === DataInterfaceType.Chat) {
        const chatAgents = agents.filter((agent) => agent.settings.interface.type === DataInterfaceType.Chat);
        const options = getSelectOptions(
          chatAgents,
          (agent) => agent.agent_id,
          (agent) => agent.name,
          objectId,
          ExchangeObjectType.Agent
        );
        return <IbSelect loading={agentsLoading} options={options} value={objectId} onChange={changeHandler} />;
      }

      if (dependency.object_subtype === DataInterfaceType.Api) {
        const apiAgents = agents.filter((agent) => agent.settings.interface.type === DataInterfaceType.Api);
        const options = getSelectOptions(
          apiAgents,
          (agent) => agent.agent_id,
          (agent) => agent.name,
          objectId,
          ExchangeObjectType.Agent
        );
        return <IbSelect loading={agentsLoading} options={options} value={objectId} onChange={changeHandler} />;
      }
    }

    return null;
  };

  const renderObjectPath = (dependency: ExchangeDependency) => {
    const { parent_type, parent_subtype, object_type, object_path } = dependency;

    const getText = (): string | null | undefined => {
      if (parent_type === ExchangeObjectType.Toolkit) {
        return translate('Path: Connector');
      }

      if (parent_type === ExchangeObjectType.Toolset) {
        if (parent_subtype === ToolsetSettingsType.McpServer || parent_subtype === ToolsetSettingsType.Elma365Toolkit) {
          return translate('Path: Connector');
        }
        if (parent_subtype === ToolsetSettingsType.Elma365General) {
          return object_type === ExchangeObjectType.Provider
            ? translate('Path: LLM: Image')
            : translate('Path: Connector');
        }
        if (parent_subtype === ToolsetSettingsType.File) {
          return translate('Path: LLM: Image');
        }
      }

      if (parent_type === ExchangeObjectType.Project && object_path) {
        const pathMap: Record<string, string> = {
          'ingestion.embedding': translate('Path: Embedding'),
          'ingestion.llm_node_parsing.llm': translate('Path: LLM: Parsing'),
          'search.llm': translate('Path: LLM: General'),
          'search.retrieve.paraphrase.llm': translate('Path: LLM: Paraphrase'),
          'search.rerank.llm_reranker.llm': translate('Path: LLM: Rerank'),
          'search.routing.selector.llm': translate('Path: LLM: Routing'),
        };
        if (object_path in pathMap) {
          return pathMap[object_path];
        }
      }

      if (parent_type === ExchangeObjectType.Agent) {
        if (parent_subtype === DataInterfaceType.Chat) {
          return translate('Path: LLM: Agent: Chat');
        }
        if (parent_subtype === DataInterfaceType.Api) {
          return translate('Path: LLM: Agent: API');
        }
      }

      if (parent_type === ExchangeObjectType.Source) {
        return dependency.parent_name;
      }

      return object_path;
    };

    return (
      <IbTypography.Paragraph ellipsis type="secondary">
        {getText()}
      </IbTypography.Paragraph>
    );
  };

  const renderObjectSelectorPath = (dependency: ExchangeDependency) => {
    if (dependency.object_type === ExchangeObjectType.Provider) {
      return translate('Select: Provider');
    }

    if (dependency.object_type === ExchangeObjectType.Connector) {
      return translate('Select: Connector');
    }

    return null;
  };

  const renderConfigureStepOrigin = (dependency: ExchangeDependency) => {
    const objectName =
      dependency.object_type === ExchangeObjectType.Connector
        ? dependency.object_name || '-'
        : dependency.object_title || dependency.object_name;
    return (
      <div className={ROW_ITEM_CLASS_NAME}>
        <div className={ROW_ITEM_PATH_CLASS_NAME}>{renderObjectPath(dependency)}</div>
        <div className={ROW_ITEM_OBJECT_CLASS_NAME}>
          {renderTooltip(objectName)}
          {dependency.object_title && dependency.object_title !== objectName && (
            <>
              <IbDivider type="vertical" />
              {renderTooltip(dependency.object_title, { disabled: true })}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderConfigureStepObject = (dependency: ExchangeDependency, index: number) => {
    const hasError = getConfigureSelectHasError(dependency);
    const errorRef = hasError
      ? (el: HTMLDivElement | null) => {
          if (el && !errorRefsRef.current.includes(el)) {
            errorRefsRef.current.push(el);
          }
        }
      : undefined;

    return (
      <div ref={errorRef} className={ROW_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph ellipsis type="secondary">
          {renderObjectSelectorPath(dependency)}
        </IbTypography.Paragraph>
        {renderObjectSelector(dependency, onConfigureSelectChange(index))}
      </div>
    );
  };

  const renderSetupStepOrigin = (dependency: ExchangeDependency) => {
    const iconName = getObjectIconName(dependency);
    return (
      <div className={ROW_ITEM_CLASS_NAME}>
        <div className={ROW_ITEM_OBJECT_CLASS_NAME}>
          {iconName && <IbIcon iconName={iconName} size={20} />}
          {renderTooltip(dependency.object_name)}
          {dependency.object_title && dependency.object_title !== dependency.object_name && (
            <>
              <IbDivider type="vertical" />
              {renderTooltip(dependency.object_title, { disabled: true })}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderSetupStepObject = (dependency: ExchangeDependency) => (
    <div className={ROW_ITEM_CLASS_NAME}>
      {renderObjectSelector(dependency, onSetupSelectChange(dependency.object_code || ''))}
    </div>
  );

  const renderSetupStepDependency = (dependency: ExchangeDependency) => {
    if (dependency.object_type === dependency.parent_type) {
      return null;
    }

    const index = dependencyIndexMap.get(dependency) as number;
    return (
      <div key={index} className={ROW_CLASS_NAME}>
        {renderSetupStepOrigin(dependency)}
        <div className={ROW_ICON_CLASS_NAME}>
          <IbIcon iconName="arrow-right" />
        </div>
        {renderSetupStepObject(dependency)}
      </div>
    );
  };

  const renderSetupStepDependencies = (dependency: ExchangeDependency) => {
    if (dependency.object_type !== ExchangeObjectType.Project) {
      return renderSetupStepDependency(dependency);
    }

    const sources = dependency.object_code ? setupStepSources.get(dependency.object_code) : null;
    if (!sources) {
      return (
        <>
          <IbDivider type="horizontal" />
          {renderSetupStepDependency(dependency)}
        </>
      );
    }

    return (
      <>
        <IbDivider type="horizontal" />
        <div className={EXTRA_CLASS_NAME}>
          {renderSetupStepDependency(dependency)}
          <IbTypography>
            <IbTypography.Paragraph type="secondary">{translate('Smart Search sources')}</IbTypography.Paragraph>
          </IbTypography>
          {Array.from(sources.values()).map(renderSetupStepDependency)}
          <div className={EXTRA_CHECKBOX_CLASS_NAME}>
            <IbCheckbox
              value={dependency.extra_data?.[INGEST_SOURCES_EXTRA_KEY]}
              onChange={onSetupExtraChange(dependency.object_code || '', INGEST_SOURCES_EXTRA_KEY)}
            />
            <IbTypography>
              <IbTypography.Paragraph type="secondary">{translate('Extra: Ingest sources')}</IbTypography.Paragraph>
            </IbTypography>
          </div>
          <div className={EXTRA_CHECKBOX_CLASS_NAME}>
            <IbCheckbox
              value={dependency.extra_data?.[DELETE_SOURCES_EXTRA_KEY]}
              onChange={onSetupExtraChange(dependency.object_code || '', DELETE_SOURCES_EXTRA_KEY)}
            />
            <IbTypography>
              <IbTypography.Paragraph type="secondary">{translate('Extra: Delete sources')}</IbTypography.Paragraph>
            </IbTypography>
          </div>
        </div>
      </>
    );
  };

  const renderConfigureStepDependency = (dependency: ExchangeDependency) => {
    if (dependency.object_type === dependency.parent_type) {
      return null;
    }

    const index = dependencyIndexMap.get(dependency) as number;
    return (
      <div key={index} className={ROW_CLASS_NAME}>
        {renderConfigureStepOrigin(dependency)}
        <div className={ROW_ICON_CLASS_NAME}>
          <IbIcon iconName="link-in" />
        </div>
        {renderConfigureStepObject(dependency, index)}
      </div>
    );
  };

  const renderConfigureStepSubGroupTitle = (groupType: ExchangeObjectType, groupSubType: string, groupName: string) => {
    if (groupType === ExchangeObjectType.Toolset) {
      const toolsetIcon = getToolsetTypeIconName(groupSubType as ToolsetSettingsType);
      const getName = () => {
        const toolsetType = groupSubType as ToolsetSettingsType;
        if (toolsetType === ToolsetSettingsType.McpServer) {
          return groupName;
        }

        return getToolsetTypeName(toolsetType);
      };

      return (
        <>
          <IbIcon iconName={toolsetIcon} />
          {getName()}
        </>
      );
    }

    return groupName;
  };

  const renderConfigureStepSubGroupDependencies = (
    groupType: ExchangeObjectType,
    groupSubType: string,
    groupName: string,
    groupDependencies: ExchangeDependency[],
    groupId: string
  ) => {
    return (
      <div key={groupId} className={SUB_GROUP_CLASS_NAME}>
        <IbTypography.Paragraph strong className={SUB_GROUP_TITLE_CLASS_NAME} type="secondary">
          {renderConfigureStepSubGroupTitle(groupType, groupSubType, groupName)}
        </IbTypography.Paragraph>
        {groupDependencies.map(renderConfigureStepDependency)}
      </div>
    );
  };

  const renderConfigureStepGroupDependencies = (
    groupName: string,
    groupDependencies: ExchangeDependency[],
    groupId: string
  ) => {
    const subGroups: Record<
      string,
      { type: ExchangeObjectType; sub_type: string; name: string; deps: ExchangeDependency[] }
    > = {};
    for (const dependency of groupDependencies) {
      const subGroupId =
        dependency.parent_type === ExchangeObjectType.Source ? dependency.object_type : dependency.parent_code;
      if (subGroupId === groupId) continue;
      if (!subGroups[subGroupId]) {
        const subGroupName =
          dependency.parent_type === ExchangeObjectType.Source
            ? translate('Smart Search sources')
            : dependency.parent_name;
        subGroups[subGroupId] = {
          type: dependency.parent_type,
          sub_type: dependency.parent_subtype,
          name: subGroupName,
          deps: [],
        };
      }
      subGroups[subGroupId].deps.push(dependency);
    }

    return (
      <div key={groupId} className={GROUP_CLASS_NAME}>
        <IbTypography.Paragraph strong className={GROUP_TITLE_CLASS_NAME}>
          {groupName}
        </IbTypography.Paragraph>
        {groupDependencies
          .filter((dependency) => dependency.parent_code === groupId)
          .map(renderConfigureStepDependency)}
        {Object.entries(subGroups).map(([subGroupId, subGroup]) =>
          renderConfigureStepSubGroupDependencies(
            subGroup.type,
            subGroup.sub_type,
            subGroup.name,
            subGroup.deps,
            subGroupId
          )
        )}
      </div>
    );
  };

  const renderSetupStepSectionContent = (icon: ReactNode, title: string, sectionDependencies: ExchangeDependency[]) => (
    <div className={SECTION_CLASS_NAME}>
      <div className={SECTION_HEADER_CLASS_NAME}>
        {icon}
        <IbTypography.Paragraph strong>{title}</IbTypography.Paragraph>
      </div>
      <div className={GROUP_CLASS_NAME}>{sectionDependencies.map(renderSetupStepDependencies)}</div>
    </div>
  );

  const renderConfigureStepSectionContent = (
    icon: ReactNode,
    title: string,
    sectionDependencies: ExchangeDependency[]
  ) => {
    const groups: Record<string, { name: string; deps: ExchangeDependency[] }> = {};
    for (const dependency of sectionDependencies) {
      const groupId = dependency.origin_code || dependency.parent_code;
      if (!groups[groupId]) {
        groups[groupId] = { name: dependency.parent_name, deps: [] };
      }
      groups[groupId].deps.push(dependency);
    }

    return (
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_HEADER_CLASS_NAME}>
          {icon}
          <IbTypography.Paragraph strong>{title}</IbTypography.Paragraph>
        </div>
        {Object.entries(groups).map(([groupId, group]) =>
          renderConfigureStepGroupDependencies(group.name, group.deps, groupId)
        )}
      </div>
    );
  };

  const getSetupStepSection = (value: {
    id: string;
    icon: ReactNode;
    title: string;
    filter: (d: ExchangeDependency) => boolean;
  }): IIbScrollSpySection => ({
    id: value.id,
    title: value.title,
    content: renderSetupStepSectionContent(value.icon, value.title, setupStepDependencies.filter(value.filter)),
  });

  const getConfigureStepSection = (value: {
    id: string;
    icon: ReactNode;
    title: string;
    filter: (d: ExchangeDependency) => boolean;
  }): IIbScrollSpySection => ({
    id: value.id,
    title: value.title,
    content: renderConfigureStepSectionContent(value.icon, value.title, configureStepDependencies.filter(value.filter)),
  });

  const getSetupStepSections = () => {
    const sections = [
      {
        id: 'agents',
        icon: <IbIcon iconName="user-business" />,
        title: translate('Section: Agents'),
        filter: (dependency: ExchangeDependency) =>
          dependency.object_type === ExchangeObjectType.Agent && dependency.object_subtype === DataInterfaceType.Chat,
      },
      {
        id: 'ai-actions',
        icon: <IbIcon iconName="worker" />,
        title: translate('Section: AI-Actions'),
        filter: (dependency: ExchangeDependency) =>
          dependency.object_type === ExchangeObjectType.Agent && dependency.object_subtype === DataInterfaceType.Api,
      },
      {
        id: 'projects',
        icon: <IbIcon iconName="optimize" />,
        title: translate('Section: Smart Search'),
        filter: (dependency: ExchangeDependency) => dependency.object_type === ExchangeObjectType.Project,
      },
      {
        id: 'toolkits',
        icon: <IbIcon iconName="toolkit" />,
        title: translate('Section: Toolkits'),
        filter: (dependency: ExchangeDependency) => dependency.object_type === ExchangeObjectType.Toolkit,
      },
    ];

    return sections.filter((section) => setupStepDependencies.some(section.filter)).map(getSetupStepSection);
  };

  const getConfigureStepSections = () => {
    const sections = [
      {
        id: 'agents',
        icon: <IbIcon iconName="user-business" />,
        title: translate('Section: Agents'),
        filter: (dependency: ExchangeDependency) =>
          dependency.origin_type === ExchangeObjectType.Agent && dependency.origin_subtype === DataInterfaceType.Chat,
      },
      {
        id: 'ai-actions',
        icon: <IbIcon iconName="worker" />,
        title: translate('Section: AI-Actions'),
        filter: (dependency: ExchangeDependency) =>
          dependency.origin_type === ExchangeObjectType.Agent && dependency.origin_subtype === DataInterfaceType.Api,
      },
      {
        id: 'projects',
        icon: <IbIcon iconName="optimize" />,
        title: translate('Section: Smart Search'),
        filter: (dependency: ExchangeDependency) => dependency.origin_type === ExchangeObjectType.Project,
      },
      {
        id: 'toolkits',
        icon: <IbIcon iconName="toolkit" />,
        title: translate('Section: Toolkits'),
        filter: (dependency: ExchangeDependency) => dependency.origin_type === ExchangeObjectType.Toolkit,
      },
    ];

    return sections.filter((section) => configureStepDependencies.some(section.filter)).map(getConfigureStepSection);
  };

  const renderSetupStepContent = () => {
    const sections = getSetupStepSections();
    if (!sections.length) {
      return null;
    }

    return <IbScrollSpy navPosition="right" sections={sections} />;
  };

  const renderConfigureStepContent = () => {
    errorRefsRef.current = [];
    const sections = getConfigureStepSections();
    if (!sections.length) {
      return null;
    }

    return <IbScrollSpy navPosition="right" sections={sections} />;
  };

  const renderContent = () => {
    if (step === DependenciesModalStep.Setup) {
      return renderSetupStepContent();
    }

    if (step === DependenciesModalStep.Configure) {
      return renderConfigureStepContent();
    }

    return null;
  };

  const renderButtons = () => {
    if (step === DependenciesModalStep.Setup) {
      return (
        <>
          <IbButton onClick={onNextClick}>{translate('Button: Next')}</IbButton>
          <IbButton type="secondary" onClick={onCancel}>
            {translate('Button: Cancel')}
          </IbButton>
        </>
      );
    }

    if (step === DependenciesModalStep.Configure) {
      return (
        <>
          <IbButton onClick={onFinishClick}>{translate('Button: Finish')}</IbButton>
          {setupStepDependencies.length > 1 ? (
            <IbButton type="secondary" onClick={onBackClick}>
              {translate('Button: Back')}
            </IbButton>
          ) : (
            <IbButton type="secondary" onClick={onCancel}>
              {translate('Button: Cancel')}
            </IbButton>
          )}
        </>
      );
    }

    return null;
  };

  const renderTitle = () => {
    if (step === DependenciesModalStep.Setup) {
      return translate('Title: Import Setup');
    }

    if (step === DependenciesModalStep.Configure) {
      return (
        <div className={TITLE_CLASS_NAME}>
          {translate('Title: Configure Dependencies')}
          {!!unselectedCount && (
            <div className={UNSELECTED_ALERT_CLASS_NAME}>
              <IbTypography.Paragraph error type="descriptor">
                {translate('No connections selected', { count: unselectedCount })}
              </IbTypography.Paragraph>
              <IbButton className={UNSELECTED_ALERT_BUTTON_CLASS_NAME} type="link" onClick={onScrollToFirstError}>
                {translate('Button: Follow')}
              </IbButton>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const renderConfirmButtons = () => (
    <>
      <IbButton key="finish-confirm" status="error" onClick={onConfirmFinish}>
        {translate('Button: Finish')}
      </IbButton>
      <IbButton key="finish-cancel" type="secondary" onClick={onConfirmCancel}>
        {translate('Button: Cancel')}
      </IbButton>
    </>
  );

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderButtons()}
      position={IbModalPosition.Centered}
      title={renderTitle()}
      visible={visible}
      onCancel={onCancel}
    >
      {renderContent()}
      <IbConfirmModal
        footer={renderConfirmButtons()}
        title={translate('Confirm: Title')}
        visible={confirmVisible}
        onCancel={onConfirmCancel}
      >
        <IbTypography.Paragraph type="secondary">{translate('Confirm: Content')}</IbTypography.Paragraph>
      </IbConfirmModal>
    </IbModal>
  );
};

export default DependenciesModal;
